import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ScrollArea } from '../../ui/scroll-area';
import { 
  Clock, ChefHat, Package, Check, Plus, Receipt, 
  ArrowLeft, RefreshCw, CreditCard, Banknote 
} from 'lucide-react';

const getItemStatusBadge = (status) => {
  const configs = {
    pendiente: { color: 'bg-amber-100 text-amber-700 border-amber-300', label: 'Pendiente', icon: Clock },
    preparando: { color: 'bg-orange-100 text-orange-700 border-orange-300', label: 'Preparando', icon: ChefHat },
    listo: { color: 'bg-green-100 text-green-700 border-green-300', label: 'Listo', icon: Package },
    entregado: { color: 'bg-gray-100 text-gray-500 border-gray-300', label: 'Entregado', icon: Check }
  };
  const config = configs[status] || configs.pendiente;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} border flex items-center gap-1 text-xs`}>
      <Icon size={12} />
      {config.label}
    </Badge>
  );
};

const TableDetailView = ({ 
  mesa, 
  onBack, 
  onAddProducts, 
  onShowPayment, 
  formatDuration,
  showPaymentModal,
  onClosePaymentModal,
  onCloseMesa,
  closingMesa
}) => {
  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-50" data-testid="detail-view">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <span className="text-xl font-bold text-blue-600">{mesa.numero}</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-lg">Mesa {mesa.numero}</span>
                  {mesa.has_kitchen_ready && (
                    <Badge className="bg-orange-500 text-white animate-pulse">
                      <ChefHat size={12} className="mr-1" />
                      Cocina Lista
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {formatDuration(mesa.session.duration_minutes)} · {mesa.session.order_count} pedidos
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-green-600">{mesa.session.total.toFixed(2)}€</p>
            </div>
            <Button onClick={onAddProducts} className="bg-blue-600 hover:bg-blue-700">
              <Plus size={16} className="mr-1" />
              Anadir
            </Button>
            <Button onClick={onShowPayment} className="bg-red-600 hover:bg-red-700">
              <Receipt size={16} className="mr-1" />
              Cobrar
            </Button>
          </div>
        </div>
      </div>

      {/* Orders list */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 max-w-4xl mx-auto">
          {mesa.session.orders.map((order, idx) => (
            <Card key={order.id} className={`${
              order.estado === 'pendiente' ? 'border-amber-300 bg-amber-50/50' :
              order.estado === 'preparando' ? 'border-orange-300 bg-orange-50/50' :
              order.estado === 'listo' ? 'border-green-300 bg-green-50/50' :
              'border-gray-200'
            }`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Pedido #{idx + 1}</CardTitle>
                  {getItemStatusBadge(order.estado)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {order.items?.map((item, itemIdx) => (
                    <div 
                      key={itemIdx}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        item.is_kitchen_item && item.status === 'listo' 
                          ? 'bg-green-100 border border-green-300' 
                          : 'bg-white border'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.quantity}x {item.product_name}</span>
                        {item.is_kitchen_item && <ChefHat size={14} className="text-orange-500" />}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{item.subtotal?.toFixed(2)}€</span>
                        {getItemStatusBadge(item.status)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t flex justify-between font-medium">
                  <span>Subtotal</span>
                  <span>{order.total?.toFixed(2)}€</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt size={24} />
                Cerrar Mesa {mesa.numero}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">Total a cobrar</p>
                <p className="text-4xl font-bold text-green-600">{mesa.session.total.toFixed(2)}€</p>
              </div>
              
              <p className="text-center text-gray-600 font-medium">Metodo de pago?</p>
              
              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => onCloseMesa('efectivo')}
                  disabled={closingMesa}
                  className="h-24 flex-col gap-2 bg-green-600 hover:bg-green-700"
                >
                  {closingMesa ? <RefreshCw className="animate-spin" size={24} /> : <Banknote size={32} />}
                  <span className="text-lg">Efectivo</span>
                </Button>
                <Button
                  onClick={() => onCloseMesa('tarjeta')}
                  disabled={closingMesa}
                  className="h-24 flex-col gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  {closingMesa ? <RefreshCw className="animate-spin" size={24} /> : <CreditCard size={32} />}
                  <span className="text-lg">Tarjeta</span>
                </Button>
              </div>
              
              <Button variant="outline" onClick={onClosePaymentModal} className="w-full">
                Cancelar
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TableDetailView;
